import React, { useState, useEffect, useMemo } from 'react';
import { FacilityType, facilityTypeTitles } from '@/types/booking.types';
import { LocationDetails } from '@/services/booking.service';
import { sessionTimer } from '@/utils/sessionTimer';
import { formatBookingTimeRange } from '@/utils/timezone-utils';
import { useMemberAccess } from '@/hooks/useMemberAccess';
import { getCheckInWindowMs } from '@/utils/checkInWindow';
import { AddToCalendarButton } from '@/components/ui/molecules/AddToCalendarButton';
import { buildBookingCalendarDescription } from '@/utils/calendarExport';
import { useVersionSynced } from '@/context/VersionSyncContext';
import './SBookingCard.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface AssignedUser {
  id: string;
  crmUserId: string;
  firstName: string;
  lastName: string;
}

interface AssignedRoom {
  id: string;
  crmId: string;
  name: string;
  resourceType: string;
}

interface SBookingCardProps {
  booking?: {
    _id?: string;
    id: string;
    bookingId: string;
    facility: string;
    bookingDate: string;
    startTime: string;
    endTime: string;
    status?: string;
    coachName?: string;
    location?: LocationDetails;
    checkinTime?: string;
    timezone?: string;
    assignedUser?: AssignedUser;
    assignedRooms?: AssignedRoom[];
    isFree?: boolean;
    calendarId?: string;
    /** How early check-in opens, sent by the API per facility. */
    checkInWindowMinutes?: number;
  };
  sponsorship?: { sponsorName: string } | null;
  onCancel?: (bookingId: string) => void;
  onCheckIn?: (bookingId: string) => void;
  onReschedule?: (bookingId: string) => void;
  onViewSession?: (bookingId: string) => void;
  onVisitShop?: () => void;
  onOpenGate?: (bookingId: string) => void;
  hasBooking?: boolean;
  isCancelling?: boolean;
  isCheckingIn?: boolean;
  isRefreshing?: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getDateParts(isoString: string, tz: string) {
  const d = new Date(isoString);
  const opts = { timeZone: tz } as const;
  return {
    day: new Intl.DateTimeFormat('en-US', { ...opts, weekday: 'short' }).format(d).toUpperCase(),
    num: new Intl.DateTimeFormat('en-US', { ...opts, day: '2-digit' }).format(d),
    month: new Intl.DateTimeFormat('en-US', { ...opts, month: 'short' }).format(d).toUpperCase(),
  };
}

function formatCountdown(ms: number): string {
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return `${String(h).padStart(2, '0')} : ${String(m).padStart(2, '0')} : ${String(s).padStart(2, '0')}`;
}

function formatStartsIn(ms: number): string {
  if (ms <= 0) return 'Starting';
  const days = Math.floor(ms / 86400000);
  const hours = Math.floor((ms % 86400000) / 3600000);
  if (days > 0) return `${days}d ${String(hours).padStart(2, '0')}h`;
  const mins = Math.floor((ms % 3600000) / 60000);
  return `${String(hours).padStart(2, '0')}h ${String(mins).padStart(2, '0')}m`;
}

// ─── Component ────────────────────────────────────────────────────────────────

const SBookingCard: React.FC<SBookingCardProps> = ({
  booking,
  sponsorship,
  onCancel,
  onCheckIn,
  onReschedule,
  onVisitShop,
  onOpenGate,
  hasBooking = !!booking,
  isCancelling = false,
  isCheckingIn = false,
  isRefreshing = false,
}) => {
  const { isAccessBlocked } = useMemberAccess();
  const versionSynced = useVersionSynced();
  const tz = booking?.timezone || 'America/Denver';

  // ── Active session (checked-in) countdown ──────────────────────────────────
  const [sessionTimeRemaining, setSessionTimeRemaining] = useState('0:00:00');

  const hasActiveSession = useMemo(() => {
    if (!booking) return false;
    const active = sessionTimer.getActiveSession();
    if (!active) return false;
    return (booking._id || booking.bookingId) === active.bookingId && sessionTimer.isSessionActive();
  }, [booking]);

  const [, setCountdownTime] = useState('0:00:00');

  useEffect(() => {
    if (!hasActiveSession) return;
    const update = () => {
      const remaining = sessionTimer.getRemainingTime();
      setCountdownTime(sessionTimer.formatRemainingTime(remaining));
      if (remaining <= 0) sessionTimer.endSession();
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [hasActiveSession]);

  useEffect(() => {
    // Run whenever the card is in active/checked-in state so the timer is
    // always ready, even during the brief window before the API refetch lands.
    if (!booking || !(booking.status === 'checkedIn' || hasActiveSession)) return;
    const update = () => {
      const end = new Date(booking.endTime).getTime();
      const remaining = end - Date.now();
      if (remaining <= 0) { setSessionTimeRemaining('0:00:00'); return; }
      const h = Math.floor(remaining / 3600000);
      const m = Math.floor((remaining % 3600000) / 60000);
      const s = Math.floor((remaining % 60000) / 1000);
      setSessionTimeRemaining(`${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`);
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [booking?.status, booking?.endTime, hasActiveSession]);

  // ── Ticking clock so time-derived values (check-in window, progress bar) ──
  // recompute every second instead of freezing at mount time.
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // ── Check-in window (facility-configured lead time → end) ─────────────────
  // 30 min for bays, 5 for the short-turnaround rooms — the API decides
  // and sends it per booking. See @/utils/checkInWindow.
  const shouldShowCheckIn = useMemo(() => {
    if (!booking) return false;
    const start = new Date(booking.startTime).getTime();
    const end = new Date(booking.endTime).getTime();
    return now >= start - getCheckInWindowMs(booking) && now <= end;
  }, [booking?.startTime, booking?.endTime, booking?.facility, booking?.checkInWindowMinutes, now]);

  // ── Countdown to check-in opening ─────────────────────────────────────────
  const [checkInCountdown, setCheckInCountdown] = useState('');

  useEffect(() => {
    if (!booking || shouldShowCheckIn) return;
    const checkInOpens =
      new Date(booking.startTime).getTime() - getCheckInWindowMs(booking);
    // Only show HH:MM:SS countdown when check-in opens within 24 h.
    // Beyond that, startsIn (days/hours) is more readable.
    if (checkInOpens - Date.now() > 24 * 60 * 60 * 1000) {
      setCheckInCountdown('');
      return;
    }
    const update = () => {
      const ms = checkInOpens - Date.now();
      setCheckInCountdown(ms > 0 ? formatCountdown(ms) : '');
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [booking?.startTime, shouldShowCheckIn]);

  // ── Progress bar ───────────────────────────────────────────────────────────
  const startsIn = useMemo(
    () => (booking ? formatStartsIn(new Date(booking.startTime).getTime() - now) : ''),
    [booking?.startTime, now],
  );

  // ── Derived UI state ───────────────────────────────────────────────────────
  const shouldShowActions = useMemo(() => {
    if (!booking) return false;
    return !['completed', 'checkedIn', 'noshow', 'cancelled'].includes(
      (booking.status || '').toLowerCase(),
    );
  }, [booking?.status]);

  const statusKey = (booking?.status || '').toLowerCase() as string;
  const isTerminal = ['completed', 'noshow', 'cancelled'].includes(statusKey);
  // Only render an inline status pill for completed / noshow — never for cancelled
  const showStatusPill = statusKey === 'completed' || statusKey === 'noshow';

  const dateParts = booking ? getDateParts(booking.startTime, tz) : null;
  const bookingId = () => booking!._id || booking!.id;

  const isBayBooking = booking?.facility === 'HITTING_BAY' || booking?.facility === 'BAY';

  const locationLabel = isBayBooking
    ? ''
    : [booking?.assignedRooms?.[0]?.name, booking?.location?.name].filter(Boolean).join(' · ');

  const bayLine1 = isBayBooking
    ? (() => {
        const roomName = booking?.assignedRooms?.[0]?.name ?? '';
        const sponsorName = sponsorship?.sponsorName ?? '';
        return sponsorName ? [roomName, sponsorName].filter(Boolean).join(' | ') : roomName;
      })()
    : null;

  const bayLine2 = isBayBooking ? (booking?.location?.name ?? '') : null;

  const coachLabel = booking?.assignedUser
    ? `${booking.assignedUser.firstName} ${booking.assignedUser.lastName}`.trim()
    : (booking?.coachName || null);

  // Requires an id up front — without one there's no stable key for native
  // to later find this event by, so a tap would create a calendar entry
  // that cancel/reschedule could never locate and remove.
  const calendarBookingId = booking?._id || booking?.id;
  const calendarEvent = booking && calendarBookingId
    ? {
        title: facilityTypeTitles[booking.facility as FacilityType] || booking.facility,
        description: buildBookingCalendarDescription(coachLabel),
        location: [locationLabel, booking.location?.address].filter(Boolean).join(', ') || undefined,
        startTime: booking.startTime,
        endTime: booking.endTime,
        uid: `${calendarBookingId}@teebox`,
      }
    : null;

  // ── Checked-in / active session card ──────────────────────────────────────
  if ((booking?.status === 'checkedIn' || hasActiveSession) && booking) {
    return (
      <div className="sbc sbc--active">
        {/* Same top-row structure as other cards */}
        <div className="sbc__top">
          <div className="sbc__date sbc__date--active">
            <span className="sbc__date-day sbc__date-day--active">{dateParts?.day}</span>
            <span className="sbc__date-num sbc__date-num--active">{dateParts?.num}</span>
            <span className="sbc__date-month sbc__date-month--active">{dateParts?.month}</span>
          </div>
          <div className="sbc__info">
            <span className="sbc__facility sbc__facility--active">ACTIVE SESSION</span>
            <span className="sbc__time sbc__time--active">
              {formatBookingTimeRange(booking.startTime, booking.endTime, tz)}
            </span>
            {isBayBooking ? (
              <>
                <span className="sbc__location sbc__location--active">{bayLine1}</span>
                {bayLine2 && <span className="sbc__location sbc__location--active">{bayLine2}</span>}
              </>
            ) : (
              <span className="sbc__location sbc__location--active">{locationLabel}</span>
            )}
            {coachLabel && !isBayBooking && ![FacilityType.GYM, FacilityType.RECOVERY_ROOM].includes(booking.facility as FacilityType) && (
              <span className="sbc__coach sbc__coach--active">with {coachLabel}</span>
            )}
          </div>
        </div>

        {/* Timer — always driven by booking.endTime so it stays accurate */}
        <div className="sbc-active__timer-row">
          <span className="sbc-active__countdown">{sessionTimeRemaining}</span>
          <span className="sbc-active__timer-label">TIME REMAINING</span>
        </div>

        {/* Actions */}
        <div className="sbc__actions">
          <button className="sbc__btn sbc__btn--checkin" onClick={() => onOpenGate?.(bookingId())}>
            Open Door
          </button>
          <button className="sbc__btn sbc__btn--secondary sbc__btn--secondary-on-dark" onClick={onVisitShop}>
            Visit Shop
          </button>
        </div>
      </div>
    );
  }

  // ── Empty state ────────────────────────────────────────────────────────────
  if (!hasBooking) {
    return (
      <div className="sbc sbc--empty">
        <span className="sbc-empty__text">No bookings yet</span>
      </div>
    );
  }

  // ── Normal booking card ────────────────────────────────────────────────────
  // Dark card only when check-in window is open
  const cardClass = 'sbc sbc--light';

  return (
    <div className={cardClass} style={{ position: 'relative' }}>
      {(isCancelling || isRefreshing) && (
        <div className="sbc__loading-overlay">
          <div className="sbc__spinner" />
        </div>
      )}
      {/* Top row: date block + session info */}
      <div className="sbc__top">
        <div className="sbc__date">
          <span className="sbc__date-day">{dateParts?.day}</span>
          <span className="sbc__date-num">{dateParts?.num}</span>
          <span className="sbc__date-month">{dateParts?.month}</span>
        </div>

        <div className="sbc__info">
          <span className="sbc__facility">
            {facilityTypeTitles[booking!.facility as FacilityType] || booking!.facility}
          </span>
          <span className="sbc__time">
            {formatBookingTimeRange(booking!.startTime, booking!.endTime, tz)}
          </span>
          {isBayBooking ? (
            <>
              <span className="sbc__location">{bayLine1}</span>
              {bayLine2 && <span className="sbc__location">{bayLine2}</span>}
            </>
          ) : (
            <span className="sbc__location">{locationLabel}</span>
          )}
          {coachLabel && !isBayBooking && ![FacilityType.GYM, FacilityType.RECOVERY_ROOM].includes(booking!.facility as FacilityType) && (
            <span className="sbc__coach">with {coachLabel}</span>
          )}

          {(showStatusPill || booking?.isFree) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
              {showStatusPill && (
                <span className={`sbc__status-pill sbc__status-pill--${statusKey}`}>
                  {statusKey === 'completed' ? 'Completed' : 'No Show'}
                </span>
              )}
              {booking?.isFree && (
                <span className="sbc__status-pill sbc__status-pill--free">Free</span>
              )}
            </div>
          )}
        </div>

        {shouldShowActions && calendarEvent && versionSynced && (
          <AddToCalendarButton
            event={calendarEvent}
            variant="icon"
            label="Add to Calendar"
            className="sbc__calendar-btn"
          />
        )}
      </div>

      {/* Countdown + progress — always rendered to keep card height consistent */}
      {!isTerminal && (
        <div className="sbc__countdown">
          <div className="sbc__countdown-row">
            <span className="sbc__countdown-label">
              {shouldShowCheckIn ? 'CHECK-IN OPEN' : checkInCountdown ? 'CHECK-IN OPENS' : 'STARTS IN'}
            </span>
            <span className="sbc__countdown-value">
              {!shouldShowCheckIn && (checkInCountdown || startsIn)}
            </span>
          </div>
          <div className="sbc__progress" />
        </div>
      )}

      {/* Action buttons */}
      {shouldShowActions && (
        <div className="sbc__actions">
          {shouldShowCheckIn && !isAccessBlocked && (
            <button
              className="sbc__btn sbc__btn--checkin"
              onClick={() => onCheckIn?.(bookingId())}
              disabled={isCheckingIn}
            >
              {isCheckingIn ? 'Checking in…' : 'Check in →'}
            </button>
          )}

          {onReschedule && !isAccessBlocked && (
            <button
              className="sbc__btn sbc__btn--secondary"
              onClick={() => onReschedule(bookingId())}
            >
              Reschedule
            </button>
          )}

          {onCancel && (
            <button
              className="sbc__btn sbc__btn--secondary"
              onClick={() => onCancel(bookingId())}
              disabled={isCancelling}
            >
              Cancel
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SBookingCard;
